// src/hooks/useCollabArrows.js
import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { useEffect, useRef } from "react";

/**
 * Syncs arrows via a single Y.Map('arrows') with one key: 'list'
 * Arrow shape: { id: string, start:{x:number,y:number}, end:{x:number,y:number}, color:string }
 */
export default function useCollabArrows(projectId, onRemoteUpdate) {
  const ydocRef = useRef(null);
  const providerRef = useRef(null);
  const yMapRef = useRef(null);

  const cbRef = useRef(onRemoteUpdate);
  useEffect(() => {
    cbRef.current = onRemoteUpdate;
  }, [onRemoteUpdate]);

  // ignore our own echoes
  const lastSentHashRef = useRef("");

  const stableSort = (arr) =>
    [...arr].sort((a, b) => {
      if (a.id !== b.id) return String(a.id).localeCompare(String(b.id));
      if ((a.start?.x ?? 0) !== (b.start?.x ?? 0))
        return (a.start?.x ?? 0) - (b.start?.x ?? 0);
      if ((a.start?.y ?? 0) !== (b.start?.y ?? 0))
        return (a.start?.y ?? 0) - (b.start?.y ?? 0);
      if ((a.end?.x ?? 0) !== (b.end?.x ?? 0))
        return (a.end?.x ?? 0) - (b.end?.x ?? 0);
      if ((a.end?.y ?? 0) !== (b.end?.y ?? 0))
        return (a.end?.y ?? 0) - (b.end?.y ?? 0);
      return String(a.color || "").localeCompare(String(b.color || ""));
    });

  const normalize = (arr) =>
    stableSort(
      (Array.isArray(arr) ? arr : []).map((d) => ({
        id: String(d.id || ""),
        start: {
          x: Number.isFinite(d?.start?.x) ? Number(d.start.x) : 0,
          y: Number.isFinite(d?.start?.y) ? Number(d.start.y) : 0,
        },
        end: {
          x: Number.isFinite(d?.end?.x) ? Number(d.end.x) : 0,
          y: Number.isFinite(d?.end?.y) ? Number(d.end.y) : 0,
        },
        color: typeof d.color === "string" ? d.color : "#00E5FF",
      }))
    );

  const stableHash = (obj) => {
    try {
      return JSON.stringify(obj);
    } catch {
      return String(Date.now());
    }
  };

  useEffect(() => {
    if (!projectId) return;

    const ydoc = new Y.Doc();

    // clean socket URL (avoid trailing slashes or quotes)
    const raw = process.env.REACT_APP_SOCKET_URL || "ws://localhost:1234";
    const socketUrl = raw.replace(/['\"\s]+/g, "").replace(/\/+$/g, "");
    const room = String(projectId).trim();

    // Room is the 2nd arg (do not append to URL)
    const provider = new WebsocketProvider(socketUrl, room, ydoc);
    const yMap = ydoc.getMap("arrows"); // Y.Map with key 'list'

    const emit = () => {
      const list = yMap.get("list") || [];
      const safe = normalize(list);
      const h = stableHash(safe);
      if (h === lastSentHashRef.current) return; // ignore our own write
      cbRef.current?.(safe);
    };

    const observer = () => emit();

    yMap.observe(observer);
    emit(); // initial hydrate

    provider.on("status", () => {
      /* optional debug */
    });

    ydocRef.current = ydoc;
    providerRef.current = provider;
    yMapRef.current = yMap;

    return () => {
      try {
        yMap.unobserve(observer);
      } catch {}
      try {
        provider.destroy();
      } catch {}
      try {
        ydoc.destroy();
      } catch {}
      ydocRef.current = null;
      providerRef.current = null;
      yMapRef.current = null;
    };
  }, [projectId]);

  // Atomic replace entire arrows list
  const syncArrows = (nextArr = []) => {
    const yMap = yMapRef.current;
    const ydoc = ydocRef.current;
    if (!yMap || !ydoc) return;

    const normalized = normalize(nextArr);
    const h = stableHash(normalized);

    ydoc.transact(() => {
      yMap.set("list", normalized);
    });

    lastSentHashRef.current = h; // mark as our write
  };

  return { syncArrows };
}
